import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-totp-reprompt.css?inline';
import { withCsrf } from '../../lib/csrf.js';
import '../pk-totp-prompt/pk-totp-prompt.js';
import '../pk-confirm-typed/pk-confirm-typed.js';

/**
 * `<pk-totp-reprompt>` — TOTP step-up gate for destructive admin actions.
 *
 * Composes on top of `<pk-totp-prompt>` and adds the round-trip to the
 * server-side verifier. The existing `<pk-totp-prompt>` only collects the
 * 6-digit code — the consumer was previously responsible for the POST. This
 * component owns the POST, surfaces server errors as `error="..."` on the
 * inner prompt, and only emits `pk-totp-verified` on a successful 200 + token.
 *
 * Optionally chains with `<pk-confirm-typed>` when `confirm-typed-name` is
 * set: the typed-name gate runs first; once the user clears it the TOTP step
 * opens. `pk-totp-cancelled` fires if the user backs out of either gate.
 *
 * POST shape — per the orchestrator admin spec (§9.1):
 *
 *     POST <verify-url>
 *     Content-Type: application/json
 *     { code, action, targetId, correlationId }
 *
 * The server is expected to respond with `{ token: "..." }` (200) on success
 * or a JSON `{ error }` body on 4xx.
 *
 * Attributes:
 *   - `open`                — boolean; shows/hides the prompt.
 *   - `action-label`        — describes the gated action (e.g. `Delete user`).
 *                              Forwarded to the inner prompt's callout.
 *   - `verify-url`          — POST endpoint for code verification. Required.
 *   - `action`              — action identifier (e.g. `admin.user.delete`).
 *   - `target-id`           — record identifier the action targets.
 *   - `correlation-id`      — correlation ID echoed in the POST body and any
 *                              subsequent action call.
 *   - `confirm-typed-name`  — optional; when set, the typed-name gate runs
 *                              first using `<pk-confirm-typed>`.
 *   - `confirm-typed-md`    — optional `consequences-md` for the typed gate.
 *   - `title`               — dialog heading on the TOTP step.
 *
 * Events:
 *   - `pk-totp-verified`  — `CustomEvent<{ token: string }>` on success.
 *   - `pk-totp-cancelled` — fired when the user backs out of either gate.
 */
export class PkTotpReprompt extends HTMLElement {
    static readonly tagName = 'pk-totp-reprompt';

    static get observedAttributes(): string[] {
        return [
            'open', 'action-label', 'verify-url', 'action', 'target-id',
            'correlation-id', 'confirm-typed-name', 'confirm-typed-md', 'title',
        ];
    }

    private readonly _root: ShadowRoot;
    private _phase: 'typed' | 'totp' | 'closed' = 'closed';
    private _error = '';
    private _submitting = false;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this._syncPhaseFromOpen();
        this.render();
    }

    attributeChangedCallback(name: string): void {
        if (name === 'open') {
            this._syncPhaseFromOpen();
            if (!this.hasAttribute('open')) {
                this._error = '';
            }
        }
        this.render();
    }

    /** Programmatically open the prompt. */
    show(): void {
        this.setAttribute('open', '');
    }

    /** Programmatically close the prompt. */
    hide(): void {
        this.removeAttribute('open');
    }

    private _syncPhaseFromOpen(): void {
        if (!this.hasAttribute('open')) {
            this._phase = 'closed';
            return;
        }
        this._phase = this.getAttribute('confirm-typed-name') ? 'typed' : 'totp';
    }

    private readonly _onTypedConfirmed = (e: Event): void => {
        e.stopPropagation();
        this._phase = 'totp';
        this.render();
    };

    private readonly _onTypedCancelled = (e: Event): void => {
        e.stopPropagation();
        this._cancel();
    };

    private readonly _onTotpVerified = async (e: Event): Promise<void> => {
        e.stopPropagation();
        const code = (e as CustomEvent).detail?.code as string;
        if (!code) return;
        const url = this.getAttribute('verify-url') ?? '';
        if (!url) {
            this._error = 'No verify-url configured.';
            this.render();
            return;
        }
        this._submitting = true;
        this._error = '';
        this.render();

        try {
            const body = {
                code,
                action:        this.getAttribute('action')         ?? '',
                targetId:      this.getAttribute('target-id')      ?? '',
                correlationId: this.getAttribute('correlation-id') ?? '',
            };
            const resp = await fetch(url, withCsrf({
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify(body),
            }));
            if (!resp.ok) {
                let msg = `Verification failed (${resp.status})`;
                try {
                    const j = await resp.json() as { error?: string };
                    if (j.error) msg = j.error;
                } catch { /* keep default */ }
                this._submitting = false;
                this._error = msg;
                this.render();
                return;
            }
            const data = await resp.json() as { token?: string };
            const token = data.token ?? '';
            this._submitting = false;
            this._error = '';
            this.removeAttribute('open');
            this.dispatchEvent(new CustomEvent('pk-totp-verified', {
                detail:   { token },
                bubbles:  true,
                composed: true,
            }));
        } catch (err) {
            this._submitting = false;
            this._error = err instanceof Error ? err.message : 'Network error';
            this.render();
        }
    };

    private readonly _onTotpCancelled = (e: Event): void => {
        e.stopPropagation();
        this._cancel();
    };

    private _cancel(): void {
        this.removeAttribute('open');
        this.dispatchEvent(new CustomEvent('pk-totp-cancelled', {
            bubbles:  true,
            composed: true,
        }));
    }

    private template(): TemplateResult {
        const phase       = this._phase;
        const title       = this.getAttribute('title')              ?? 'Confirm with your authenticator code';
        const actionLabel = this.getAttribute('action-label')       ?? '';
        const typedName   = this.getAttribute('confirm-typed-name') ?? '';
        const typedMd     = this.getAttribute('confirm-typed-md')   ?? '';

        return html`
            <style>${styles}</style>

            ${typedName ? html`
                <pk-confirm-typed
                    ?open=${phase === 'typed'}
                    target-name=${typedName}
                    consequences-md=${typedMd}
                    title=${title}
                    verb=${actionLabel || 'Continue'}
                    @pk-confirmed=${this._onTypedConfirmed}
                    @pk-cancelled=${this._onTypedCancelled}
                ></pk-confirm-typed>
            ` : ''}

            <pk-totp-prompt
                ?open=${phase === 'totp'}
                ?submitting=${this._submitting}
                title=${title}
                action-label=${actionLabel}
                error=${this._error}
                verify-label=${this._submitting ? 'Verifying…' : 'Verify'}
                @verified=${this._onTotpVerified}
                @cancelled=${this._onTotpCancelled}
            ></pk-totp-prompt>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkTotpReprompt.tagName)) {
    customElements.define(PkTotpReprompt.tagName, PkTotpReprompt);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-totp-reprompt': PkTotpReprompt;
    }
}
